// src/pages/CreateOrderPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from 'aws-amplify/api';
import {
    Heading,
    Flex,
    Button,
    TextField,
    SelectField,
} from '@aws-amplify/ui-react';

export default function CreateOrderPage() {
    const navigate = useNavigate();
    const [errorMsg, setErrorMsg] = useState('');
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        // top-level fields
        orderId: '',
        orderDate: '',
        OrderStatus: '',
        totalAmount: '',
        source: 'MANUAL',
        customerId: '',

        // billing address
        billingContact: '',
        billingAddress1: '',
        billingAddress2: '',
        billingAddress3: '',
        billingSuburb: '',
        billingState: '',
        billingCountry: '',

        // line item
        price: '',
        quantity: '',
        productId: 'CUSTOM-BOX',
        Board_Grade: 'KSC',
        Box_Style: 'RSC',
        Include_Lid: 'No',

        // dimensions
        length: '',
        width: '',
        height: '',

        // new fields
        _boxesPerSheet: '',
        _totalSheets: '',
        _totalLength: '',
        _totalWidth: '',
    });

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.orderId) {
            setErrorMsg('Order ID is required');
            return;
        }

        setLoading(true);
        setErrorMsg('');

        try {
            // items array with ONE line item
            const items = [
                {
                    price: parseFloat(formData.price) || 0,
                    quantity: parseInt(formData.quantity, 10) || 0,
                    productId: formData.productId,
                    properties: {
                        Board_Grade: formData.Board_Grade,
                        Box_Style: formData.Box_Style,
                        Include_Lid: formData.Include_Lid,
                        Length: formData.length,
                        Width: formData.width,
                        Height: formData.height,
                        _boxesPerSheet: parseFloat(formData._boxesPerSheet) || 0,
                        _totalSheets: parseFloat(formData._totalSheets) || 0,
                        _totalLength: parseFloat(formData._totalLength) || 0,
                        _totalWidth: parseFloat(formData._totalWidth) || 0,
                    },
                },
            ];

            // billingAddress object
            const billingAddress = {
                Contact: formData.billingContact,
                Address1: formData.billingAddress1,
                Address2: formData.billingAddress2,
                Address3: formData.billingAddress3,
                Suburb: formData.billingSuburb,
                State: formData.billingState,
                Country: formData.billingCountry,
            };

            // final payload
            const payload = {
                orderId: formData.orderId,
                orderDate: formData.orderDate,
                OrderStatus: formData.OrderStatus,
                customerId: formData.customerId,
                billingAddress,
                totalAmount: parseFloat(formData.totalAmount) || 0,
                source: formData.source,
                items,
            };

            // Amplify v6 POST
            const restOp = post({
                apiName: 'cbx_CRUD_API',
                path: '/unifiedorders',
                options: {
                    body: payload,
                },
            });
            const response = await restOp.response;

            if (response.statusCode < 200 || response.statusCode > 299) {
                throw new Error(`HTTP ${response.statusCode} error creating order`);
            }

            alert('Order created!');
            navigate('/');
        } catch (error) {
            console.error('Create error:', error);
            setErrorMsg(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem' }}>
            <Heading level={3}>Create Order</Heading>
            {errorMsg && <div style={{ color: 'red' }}>{errorMsg}</div>}

            <form onSubmit={handleSubmit}>
                <Flex direction="column" gap="1rem" maxWidth="600px">
                    <TextField
                        label="Order ID"
                        value={formData.orderId}
                        onChange={(e) => handleChange('orderId', e.target.value)}
                        placeholder="e.g. PO#60771"
                        required
                    />
                    <TextField
                        label="Order Date"
                        value={formData.orderDate}
                        onChange={(e) => handleChange('orderDate', e.target.value)}
                        placeholder="YYYY-MM-DD or full ISO"
                    />
                    <TextField
                        label="Order Status"
                        value={formData.OrderStatus}
                        onChange={(e) => handleChange('OrderStatus', e.target.value)}
                        placeholder="Entered / Shipped / etc."
                    />
                    <TextField
                        label="Customer ID"
                        value={formData.customerId}
                        onChange={(e) => handleChange('customerId', e.target.value)}
                    />
                    <TextField
                        label="Total Amount"
                        type="number"
                        step="any"
                        value={formData.totalAmount}
                        onChange={(e) => handleChange('totalAmount', e.target.value)}
                    />
                    <SelectField
                        label="Source"
                        value={formData.source}
                        onChange={(e) => handleChange('source', e.target.value)}
                    >
                        <option value="MANUAL">Manual</option>
                        <option value="SHOPIFY">Shopify</option>
                        <option value="PO">Purchase Order</option>
                    </SelectField>

                    <Heading level={5}>Billing Address</Heading>
                    <TextField
                        label="Contact"
                        value={formData.billingContact}
                        onChange={(e) => handleChange('billingContact', e.target.value)}
                    />
                    <TextField
                        label="Address1"
                        value={formData.billingAddress1}
                        onChange={(e) => handleChange('billingAddress1', e.target.value)}
                    />
                    <TextField
                        label="Address2"
                        value={formData.billingAddress2}
                        onChange={(e) => handleChange('billingAddress2', e.target.value)}
                    />
                    <TextField
                        label="Address3"
                        value={formData.billingAddress3}
                        onChange={(e) => handleChange('billingAddress3', e.target.value)}
                    />
                    <TextField
                        label="Suburb"
                        value={formData.billingSuburb}
                        onChange={(e) => handleChange('billingSuburb', e.target.value)}
                    />
                    <TextField
                        label="State"
                        value={formData.billingState}
                        onChange={(e) => handleChange('billingState', e.target.value)}
                    />
                    <TextField
                        label="Country"
                        value={formData.billingCountry}
                        onChange={(e) => handleChange('billingCountry', e.target.value)}
                    />

                    <Heading level={5}>Single Line Item</Heading>
                    <TextField
                        label="Product ID"
                        value={formData.productId}
                        onChange={(e) => handleChange('productId', e.target.value)}
                    />
                    <TextField
                        label="Price"
                        type="number"
                        step="any"
                        value={formData.price}
                        onChange={(e) => handleChange('price', e.target.value)}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        step="1"
                        value={formData.quantity}
                        onChange={(e) => handleChange('quantity', e.target.value)}
                    />
                    <SelectField
                        label="Board Grade"
                        value={formData.Board_Grade}
                        onChange={(e) => handleChange('Board_Grade', e.target.value)}
                    >
                        <option value="KSC">KSC</option>
                        <option value="KTC">KTC</option>
                        <option value="WSC">WSC</option>
                    </SelectField>
                    <SelectField
                        label="Box Style"
                        value={formData.Box_Style}
                        onChange={(e) => handleChange('Box_Style', e.target.value)}
                    >
                        <option value="RSC">RSC</option>
                        <option value="Box no Lid">Box no Lid</option>
                        <option value="Box with Lid">Box with Lid</option>
                        <option value="5PF">5PF</option>
                        <option value="FOLF">FOLF</option>
                        <option value="HSC">HSC</option>
                    </SelectField>
                    <SelectField
                        label="Include Lid?"
                        value={formData.Include_Lid}
                        onChange={(e) => handleChange('Include_Lid', e.target.value)}
                    >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                    </SelectField>

                    <TextField
                        label="Length"
                        type="number"
                        step="any"
                        value={formData.length}
                        onChange={(e) => handleChange('length', e.target.value)}
                    />
                    <TextField
                        label="Width"
                        type="number"
                        step="any"
                        value={formData.width}
                        onChange={(e) => handleChange('width', e.target.value)}
                    />
                    <TextField
                        label="Height"
                        type="number"
                        step="any"
                        value={formData.height}
                        onChange={(e) => handleChange('height', e.target.value)}
                    />

                    <Button type="submit" variation="primary" isDisabled={loading}>
                        {loading ? 'Creating...' : 'Create Order'}
                    </Button>
                </Flex>
            </form>
        </div>
    );
}
